/**
 * One list in a group's consumption queue — sortable only in MANUAL mode.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

import { cn } from '@/lib/utils';

import { TagChips } from '../rules/TagChips';

interface Props {
  wiserId: number;
  rank: number;
  name: string;
  leadCount: number | null;
  draggable: boolean;
  /** Phase 18 — the list formula's word on this list (score, or why unranked). */
  note?: string;
  /** Phase 25 — its list tags, when a rule tests one. */
  tags?: string[];
}

export const OrderQueueRow: FC<Props> = ({
  wiserId,
  rank,
  name,
  leadCount,
  draggable,
  note,
  tags,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(wiserId), disabled: !draggable });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-md px-2 py-1 text-xs',
        isDragging && 'z-10 bg-background shadow-md',
        rank === 1 && 'bg-primary/5',
      )}
    >
      {draggable ? (
        <button
          type="button"
          aria-label={`Reorder ${name}`}
          className="cursor-grab text-muted-foreground/70 hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      ) : (
        <span className="w-3.5" />
      )}
      <span className="w-6 shrink-0 text-right font-mono tabular-nums text-muted-foreground">
        {rank}
      </span>
      <span className="min-w-0 truncate font-medium">{name}</span>
      {rank === 1 ? (
        <span className="shrink-0 rounded-full bg-primary/10 px-2 py-px text-[10px] font-semibold uppercase tracking-wider text-primary">
          first in line
        </span>
      ) : null}
      <TagChips tags={tags} />
      {note ? <span className="ml-auto shrink-0 truncate text-[11px] text-muted-foreground">{note}</span> : null}
      {/* Unknown lead count: nothing — a lone dash on every row read as noise. */}
      {leadCount != null ? (
        <span className={cn('shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground', !note && 'ml-auto')}>
          {leadCount.toLocaleString('en-US')} leads
        </span>
      ) : null}
    </div>
  );
};
